import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Select, Tag, Typography, Space,
  App as AntApp, Flex, Empty,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import type { BacklogItem } from './types';
import { CAT_INFO, PRIO_INFO, STATUS_INFO, MODULOS } from './types';
import { getToken, fmtDateTime } from './utils';
import ModalBacklogItem from './ModalBacklogItem';

const { Title, Text } = Typography;

// Backlog do sistema (anote melhorias/bugs, processe em batch).
export default function AdminBacklog() {
  const { message, modal } = AntApp.useApp();
  const [itens, setItens] = useState<BacklogItem[]>([]);
  const [sumario, setSumario] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<string>('aberto');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('');
  const [filtroPrioridade, setFiltroPrioridade] = useState<string>('');
  const [filtroModulo, setFiltroModulo] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<BacklogItem | null>(null);

  useEffect(() => { carregar(); /* eslint-disable-line */ }, [filtroStatus, filtroCategoria, filtroPrioridade, filtroModulo]);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'backlog_sys_listar',
          status: filtroStatus || undefined, categoria: filtroCategoria || undefined,
          prioridade: filtroPrioridade || undefined, modulo: filtroModulo || undefined,
          busca: busca || undefined,
        }, { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setItens(data?.itens || []);
      setSumario(data?.sumario || {});
    } finally { setLoading(false); }
  }

  function excluir(id: string) {
    modal.confirm({
      title: 'Excluir item do backlog?', okText: 'Excluir', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        const token = await getToken();
        await axios.post(`${API_URL}/agentes/exemplos`,
          { action: 'backlog_sys_excluir', id }, { headers: { Authorization: token } });
        await carregar();
      },
    });
  }
  async function mudarStatus(item: BacklogItem, novoStatus: string) {
    const token = await getToken();
    await axios.post(`${API_URL}/agentes/exemplos`,
      { action: 'backlog_sys_atualizar', id: item.id, status: novoStatus },
      { headers: { Authorization: token } });
    await carregar();
  }
  function copiarParaClaude() {
    const abertos = itens.filter(i => i.status === 'aberto' || i.status === 'em_progresso');
    if (abertos.length === 0) { message.info('Sem itens abertos pra copiar.'); return; }
    const linhas = ['# Backlog do sistema — abertos\n'];
    abertos.forEach((i, idx) => {
      const tags = [CAT_INFO[i.categoria]?.label || i.categoria, PRIO_INFO[i.prioridade]?.label || i.prioridade, i.modulo || null].filter(Boolean).join(' · ');
      linhas.push(`${idx + 1}. **${i.titulo}** _(${tags})_`);
      if (i.descricao) linhas.push(`   ${i.descricao}`);
      linhas.push(`   _id: ${i.id.slice(0, 8)} · origem: ${i.origem}_\n`);
    });
    navigator.clipboard.writeText(linhas.join('\n'));
    message.success(`Copiado! ${abertos.length} itens. Cola no Claude Code.`);
  }

  return (
    <div>
      <Flex align="center" gap={12} wrap="wrap" style={{ marginBottom: 14 }}>
        <Title level={5} style={{ margin: 0, color: '#020D7F' }}>📋 Backlog do sistema</Title>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Anote melhorias/bugs vendo o sistema funcionar — processe em batch
        </Text>
        <Space style={{ marginLeft: 'auto' }}>
          <Button size="small" type="primary" ghost onClick={copiarParaClaude}
            title="Copia itens abertos pro Claude Code processar em batch">
            📋 Copiar p/ Claude
          </Button>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCriando(true)}>
            Adicionar
          </Button>
        </Space>
      </Flex>

      <Space wrap style={{ marginBottom: 12 }}>
        {(['aberto', 'em_progresso', 'feito', 'descartado'] as const).map(s => {
          const cnt = sumario.por_status?.[s] || 0;
          const cfg = STATUS_INFO[s];
          return (
            <Tag.CheckableTag key={s} checked={filtroStatus === s}
              onChange={() => setFiltroStatus(filtroStatus === s ? '' : s)}>
              {cfg.label}: {cnt}
            </Tag.CheckableTag>
          );
        })}
        {filtroStatus && <Button size="small" type="link" onClick={() => setFiltroStatus('')}>Mostrar todos</Button>}
      </Space>

      <Flex gap={6} wrap="wrap" style={{ marginBottom: 14 }}>
        <Input value={busca} onChange={e => setBusca(e.target.value)} onPressEnter={carregar}
          placeholder="Buscar..." prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          size="small" style={{ flex: 1, minWidth: 200, maxWidth: 320 }} />
        <Select value={filtroCategoria || undefined} onChange={(v) => setFiltroCategoria(v || '')}
          placeholder="Todas categorias" allowClear size="small" style={{ width: 160 }}
          options={Object.entries(CAT_INFO).map(([k, v]) => ({ label: `${v.icon} ${v.label}`, value: k }))} />
        <Select value={filtroPrioridade || undefined} onChange={(v) => setFiltroPrioridade(v || '')}
          placeholder="Todas prioridades" allowClear size="small" style={{ width: 150 }}
          options={Object.entries(PRIO_INFO).map(([k, v]) => ({ label: v.label, value: k }))} />
        <Select value={filtroModulo || undefined} onChange={(v) => setFiltroModulo(v || '')}
          placeholder="Todos módulos" allowClear size="small" style={{ width: 140 }}
          options={MODULOS.map(m => ({ label: m, value: m }))} />
      </Flex>

      {loading ? <Card loading /> : itens.length === 0 ? (
        <Empty description={
          <Text style={{ fontSize: 12 }}>
            Nenhum item.{' '}
            <Button type="link" size="small" onClick={() => setCriando(true)}>adicione o primeiro</Button>
          </Text>
        } />
      ) : (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {itens.map(i => {
            const cat = CAT_INFO[i.categoria] || CAT_INFO.melhoria;
            const prio = PRIO_INFO[i.prioridade] || PRIO_INFO.media;
            const stat = STATUS_INFO[i.status] || STATUS_INFO.aberto;
            const concluido = i.status === 'feito' || i.status === 'descartado';
            return (
              <Card key={i.id} size="small" bodyStyle={{ padding: 10 }}
                style={{ opacity: concluido ? 0.65 : 1 }}>
                <Flex align="center" gap={8} wrap="wrap" style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: 16 }}>{cat.icon}</span>
                  <Text strong style={{
                    fontSize: 13, flex: 1, textDecoration: concluido ? 'line-through' : 'none',
                  }}>{i.titulo}</Text>
                  <Tag color={cat.tagColor}>{cat.label}</Tag>
                  <Tag style={{ color: prio.cor, borderColor: prio.cor }}>{prio.label}</Tag>
                  {i.modulo && <Tag color="default">📦 {i.modulo}</Tag>}
                  <Tag color={stat.tagColor}>{stat.label}</Tag>
                </Flex>
                {i.descricao && (
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginLeft: 24, marginBottom: 6, whiteSpace: 'pre-wrap' }}>
                    {i.descricao}
                  </Text>
                )}
                <Flex align="center" gap={6} wrap="wrap" style={{ marginLeft: 24 }}>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {i.origem === 'telegram' ? '📱' : i.origem === 'dwight' ? '◇' : '✍️'} {i.origem}
                    {' · '}{fmtDateTime(i.criado_em)}
                    {i.criado_por && ` · ${i.criado_por.split('@')[0]}`}
                    {i.resolvido_em && ` · ✓ ${fmtDateTime(i.resolvido_em)}`}
                  </Text>
                  <Space size={4} style={{ marginLeft: 'auto' }}>
                    {i.status === 'aberto' && (
                      <Button size="small" type="default" onClick={() => mudarStatus(i, 'em_progresso')}
                        style={{ color: '#0930D0', borderColor: '#0930D0', fontSize: 10 }}>▶ Iniciar</Button>
                    )}
                    {i.status === 'em_progresso' && (
                      <Button size="small" onClick={() => mudarStatus(i, 'aberto')}
                        style={{ color: '#B07500', borderColor: '#B07500', fontSize: 10 }}>⏸ Pausar</Button>
                    )}
                    {(i.status === 'aberto' || i.status === 'em_progresso') && (
                      <Button size="small" onClick={() => mudarStatus(i, 'feito')}
                        style={{ color: '#0a7f4a', borderColor: '#0a7f4a', fontSize: 10 }}>✓ Feito</Button>
                    )}
                    {(i.status === 'aberto' || i.status === 'em_progresso') && (
                      <Button size="small" onClick={() => mudarStatus(i, 'descartado')} style={{ fontSize: 10 }}>× Descartar</Button>
                    )}
                    {(i.status === 'feito' || i.status === 'descartado') && (
                      <Button size="small" onClick={() => mudarStatus(i, 'aberto')}
                        style={{ color: '#B07500', borderColor: '#B07500', fontSize: 10 }}>↺ Reabrir</Button>
                    )}
                    <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(i)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => excluir(i.id)} />
                  </Space>
                </Flex>
              </Card>
            );
          })}
        </Space>
      )}

      {(criando || editando) && (
        <ModalBacklogItem item={editando}
          onClose={() => { setCriando(false); setEditando(null); }}
          onSalvo={() => { setCriando(false); setEditando(null); carregar(); }} />
      )}
    </div>
  );
}
